import React, {FC, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import moment from 'moment';
import actions from 'store/actions';
import {INonMember} from 'types/nonMember';
import {StatusType} from 'store/status/types';
import {ModalViewEnum} from '.';
import Controls from '../../../controls';
import IStoreState from 'store/types';
import {PublicEventType} from 'enums/public_event_type';
import site from 'settings/site';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faSpinner} from '@fortawesome/free-solid-svg-icons';

interface NonMemberType extends Partial<INonMember> {
  leadRequestLocation: string;
}

interface SignNonMemberProfileProps {
  onCancel: () => void;
  eventId: string;
  date: string;
  lead: NonMemberType;
  setView: React.Dispatch<React.SetStateAction<ModalViewEnum>>;
}

export const SignNonMemberProfile: FC<SignNonMemberProfileProps> = ({onCancel, eventId, date, lead, setView}) => {
  const dispatch = useDispatch();

  const memberId = useSelector((state: IStoreState) => state.auth.memberId);
  const events = useSelector((state: IStoreState) => state.publicEvents.list);
  const [isProcessing, setIsProcessing] = useState(false);
  const [memberSignature, setMemberSignature] = useState<string | null>(null);
  const [guardianSignature, setGuardianSignature] = useState<string | null>(null);
  const [isSignatureValid, setIsSignatureValid] = useState(false);

  function onChange<T>(setter: React.Dispatch<React.SetStateAction<T>>) {
    return (value: string | null) => setter(value as T);
  }

  const memberAge = moment().diff(moment(lead.personalDetails?.dateOfBirth ?? '', 'YYYY-MM-DD'), 'years');

  const validateSignature = () => {
    if (!memberSignature || (memberAge < site.ageOfMajority && !guardianSignature)) {
      setIsSignatureValid(true);
      dispatch(actions.status.status({message: 'Please fill all signature fields.', type: StatusType.ERROR}));

      return false;
    }

    setIsSignatureValid(false);
    dispatch(actions.status.status());

    return true;
  };

  const showParentSignature = memberAge < site.ageOfMajority;
  const calendarEvent = events.find((e) => e.event.id === eventId);

  const onSign = async () => {
    setIsProcessing(true);
    if (!validateSignature() || !calendarEvent) {
      setIsProcessing(false);
      return;
    }

    const bookArgs = {
      eventId,
      eventTitle: calendarEvent.title,
      date,
      newLeadData: lead,
      signatures: {memberSignature, guardianSignature},
      action: 'book' as const,
      eventType: calendarEvent.event.type,
    };

    try {
      await actions.publicEvents.book(
        {
          ...bookArgs,
          hasPrice: calendarEvent.event.leadPrice > 0,
          memberId: calendarEvent.event.type === PublicEventType.SEMINAR ? undefined : memberId,
        },
        dispatch
      );
    } finally {
      setIsProcessing(false);
      onCancel();
    }
  };

  return (
    <div className="full-screen">
      <div className="p-3 h-100">
        <div className="form-md">
          <p>
            I consent to the terms and conditions and confirm that my attention has been drawn to the same and I am 21
            years of age or over:
          </p>
          <div className="row">
            <p className="col-sm-3">Signature of Guest:</p>
            <div className="col-sm-9 embed-responsive embed-responsive-21by9 mx-3">
              <div className="embed-responsive-item">
                <Controls.Signature value={memberSignature} onChange={onChange(setMemberSignature)} />
              </div>
            </div>
          </div>
          {showParentSignature && (
            <div>
              <p className="mt-3">As legal guardian of the participant I consent to the terms and conditions:</p>
              <div className="row">
                <p className="col-sm-3">Signature of Parent / Guardian:</p>
                <div className="col-sm-9 embed-responsive embed-responsive-21by9 mx-3">
                  <div className="embed-responsive-item">
                    <Controls.Signature value={guardianSignature} onChange={onChange(setGuardianSignature)} />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="pt-3">
        <div className="float-right">
          {isProcessing && <FontAwesomeIcon icon={faSpinner} pulse />}
          <button
            className="btn btn-primary ml-2"
            type="button"
            onClick={onSign}
            disabled={isSignatureValid || isProcessing}>
            Sign
          </button>
          <button
            className="btn btn-secondary ml-2"
            type="button"
            onClick={() => setView(ModalViewEnum.CREATE)}
            disabled={isProcessing}>
            Back
          </button>
        </div>
      </div>
    </div>
  );
};
